import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ActionIcon,
  Button,
  Group,
  Switch,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { openModal } from '@mantine/modals';
import { IconPlus, IconSearch, IconSitemap, IconX } from '@tabler/icons-react';

import {
  GetMedicalOrganizationsRequestParams,
  MedicalOrganization,
  MedicalOrganizationsSortBy,
} from '@interfaces/medicalOrganizations.interface';

import {
  useCountMedicalOrganizationsQuery,
  useGetManyMedicalOrganizationsQuery,
} from '@api/medicalOrganizations.api';
import { useUpdateUserMutation } from '@api/users.api';

import useLayoutProps from '@hooks/useLayoutProps';
import useStateWithDebounce from '@hooks/useStateWithDebouce';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UpsertMedicalOrganizationForm, {
  UPSERT_MEDICAL_ORGANIZATION_FORM_MODAL_ID,
} from '@components/medicalOrganizations/UpsertMedicalOrganizationForm';

export default function MedicalOrganizationsList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const canCreateMedicalOrganizations = useUserPermissionGuard(
    'MEDICAL_ORGANIZATIONS.CREATE',
  );

  const canEditUsers = useUserPermissionGuard('USERS.UPDATE');

  const navigate = useNavigate();

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetMedicalOrganizationsRequestParams>({
    page: +1,
    pageLength: +10,
    sortBy: 'user.name',
    sortOrder: 'asc',
  });

  const [nameSearch, setNameSearch, nameSearchDebounced] =
    useStateWithDebounce('');
  const [emailSearch, setEmailSearch, emailSearchDebounced] =
    useStateWithDebounce('');
  const [vatNumberSearch, setVatNumberSearch, vatNumberSearchDebounced] =
    useStateWithDebounce('');
  const [phoneSearch, setPhoneSearch, phoneSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<MedicalOrganization>
  >({
    columnAccessor: 'user.name',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as MedicalOrganizationsSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const {
    data: medicalOrganizationsCount = { count: 0 },
    isLoading: isLoadingCount,
  } = useCountMedicalOrganizationsQuery({
    nameSearch: nameSearchDebounced,
    emailSearch: emailSearchDebounced,
    vatNumberSearch: vatNumberSearchDebounced,
    phoneSearch: phoneSearchDebounced,
  });

  const { data = [], isLoading } = useGetManyMedicalOrganizationsQuery({
    ...filters,
    nameSearch: nameSearchDebounced,
    emailSearch: emailSearchDebounced,
    vatNumberSearch: vatNumberSearchDebounced,
    phoneSearch: phoneSearchDebounced,
  });

  const [updateUser, { isLoading: isLoadingUpdateUser }] =
    useUpdateUserMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleAddMedicalOrganizationClick = useCallback(() => {
    openModal({
      modalId: UPSERT_MEDICAL_ORGANIZATION_FORM_MODAL_ID,
      title: 'Registra organizzazione medica',
      children: (
        <UpsertMedicalOrganizationForm
          afterSubmit={({ id }) => navigate(`/organizzazioni-mediche/${id}`)}
        />
      ),
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconSitemap />
        Organizzazioni mediche{' '}
        {!isLoadingCount ? `(${medicalOrganizationsCount.count})` : ''}
      </Group>
    ),
    [medicalOrganizationsCount.count, isLoadingCount],
  );

  const rightSection = useMemo(
    () =>
      canCreateMedicalOrganizations ? (
        <Button
          leftSection={<IconPlus />}
          onClick={handleAddMedicalOrganizationClick}
        >
          Registra organizzazione medica
        </Button>
      ) : (
        <></>
      ),
    [handleAddMedicalOrganizationClick, canCreateMedicalOrganizations],
  );

  useLayoutProps({
    title,
    rightSection,
  });

  return (
    <>
      <DataTable
        /* General */
        striped
        highlightOnHover
        noRecordsText="Nessun organizzazione medica trovata"
        minHeight={data.length === 0 ? 200 : undefined}
        /* Data */
        fetching={isLoading || isLoadingCount}
        columns={[
          {
            accessor: 'active',
            title: 'Attivo',
            width: '0%',
            render: (record) => (
              <Tooltip
                label={
                  !canEditUsers
                    ? "Non hai i permessi per modificare l'utente"
                    : record.user.activationTokenCreatedAt
                      ? "L'utente non ha ancora attivato il suo account"
                      : record.user.active
                        ? 'Disabilita'
                        : 'Abilita'
                }
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <Switch
                    disabled={
                      !canEditUsers ||
                      record.user.activationTokenCreatedAt !== null ||
                      isLoadingUpdateUser
                    }
                    checked={record.user.active}
                    onChange={(e) => {
                      e.stopPropagation();
                      updateUser({
                        id: record.user.id,
                        body: { active: e.target.checked },
                      });
                    }}
                  />
                </div>
              </Tooltip>
            ),
          },
          {
            accessor: 'user.name',
            title: 'Nome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca nome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  nameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={nameSearch}
                onChange={(e) => setNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'user.email',
            title: 'Email',
            filter: (
              <TextInput
                placeholder="Ricerca email"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  emailSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setEmailSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'vatNumber',
            title: 'Partita IVA',
            filter: (
              <TextInput
                placeholder="Ricerca partita IVA"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  vatNumberSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setVatNumberSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={vatNumberSearch}
                onChange={(e) => setVatNumberSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'phone',
            title: 'Telefono',
            filter: (
              <TextInput
                placeholder="Ricerca telefono"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  phoneSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setPhoneSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.currentTarget.value)}
              />
            ),
          },
        ]}
        records={data}
        onRowClick={({ record }) => {
          navigate(`/organizzazioni-mediche/${record.id}`);
        }}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={medicalOrganizationsCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
